import {
  ArrowRight,
  ArrowRightLeft,
  CalendarClock,
  ClipboardList,
  Copy,
  CreditCard,
  ExternalLink,
  FileText,
  History,
  Mail,
  Phone,
  Plus,
  Receipt,
  Truck,
  Users,
  type LucideIcon,
} from 'lucide-react';
import type {
  ArticleResponse,
  ContactDirectoryItem,
  ContactResponse,
  CustomerResponse,
  MoneyDto,
  OpenIncomingEntry,
  OpenItemResponse,
  Permission,
  ProjectResponse,
  PurchaseOrderResponse,
  SalesDocumentResponse,
  SearchHit,
} from '@proerp/shared';
import { formatDate } from '@/lib/format';
import { formatMoney } from '@/lib/money';
import { unitLabel } from '@/features/inventory/articleMeta';
import { PROJECT_AMPEL_OPTIONS, projectStatusLabel } from '@/features/projects/statusBadge';
import { convertTargets, kindLabel, statusLabel } from '@/features/sales/salesMeta';
import { purchaseKindLabel, purchaseStatusLabel } from '@/features/procurement/procurementMeta';
import { featureRoute } from './searchApi';

/**
 * Antworten & Aktionen der Suche (Vorschlag C, Nutzerwunsch 2026-10-05): Zum
 * markierten Treffer steht rechts gleich die Antwort, wegen der man meist sucht
 * („wie viele sind frei?", „was ist noch offen?"), und eine Liste dessen, was man
 * damit tun kann.
 *
 * **Aktionen schreiben nie selbst.** Sie springen auf die Seite, deren Dialog die
 * Sache erledigt — vorbelegt über die Adresse (`?neu=1&customerId=…`,
 * `?aktion=wandeln`); bestätigt wird dort, mit den Rechten, dem Vier-Augen-Prinzip
 * und dem Audit-Log dieser Seite. Nur Kopieren und „im Hintergrund öffnen"
 * passieren direkt.
 *
 * Was schon im Treffer steht (Nummer, Anschrift im Untertitel), wiederholen die
 * Fakten nicht — die Karte ist schmal, und die Aktionen sollen sichtbar bleiben.
 *
 * Reine Funktionen: Die Daten lädt `useHitAnswer`, gezeichnet wird in `HitAnswerCard`.
 */

export type FactTone = 'success' | 'warning' | 'danger';

export interface AnswerFact {
  label: string;
  value: string;
  tone?: FactTone;
  /** Wert lässt sich mit einem Knopf daneben kopieren (Telefon, E-Mail). */
  copyValue?: string;
}

/** Was ⌘/Strg+C (bzw. eine Kopier-Aktion) in die Zwischenablage legt. */
export interface CopyTarget {
  value: string;
  /** Wofür der Wert steht — für Beschriftung und Rückmeldung („Belegnummer kopiert"). */
  what: string;
}

interface ActionBase {
  id: string;
  label: string;
  icon: LucideIcon;
  /** Kurzer Zusatz unter der Beschriftung, z. B. wo man landet. */
  hint?: string;
}

export type HitAction =
  /** Treffer selbst öffnen (↵). */
  | (ActionBase & { kind: 'open'; to: string })
  /** Treffer als Tab im Hintergrund öffnen (⌘/Strg+↵), die Suche bleibt offen. */
  | (ActionBase & { kind: 'background'; to: string })
  /** Andere Seite der App — als echter Anker (CLAUDE.md §9). */
  | (ActionBase & { kind: 'link'; to: string })
  /** `mailto:`/`tel:` — verlässt die App nicht, öffnet das Programm des Systems. */
  | (ActionBase & { kind: 'external'; href: string })
  | (ActionBase & { kind: 'copy'; copy: CopyTarget });

export interface HitAnswer {
  /** Die eine Antwort, groß oben — `null`, wenn der Typ keine hat. */
  headline: AnswerFact | null;
  facts: AnswerFact[];
  /** Alle Aktionen in Reihenfolge; die ersten beiden sind immer Öffnen und Hintergrund-Tab. */
  actions: HitAction[];
  /** Ziel von ⌘/Strg+C — die Nummer des Datensatzes, wo es eine gibt. */
  copy: CopyTarget | null;
}

export type Can = (permission: Permission) => boolean;

const num = (value: number) => new Intl.NumberFormat('de-AT', { maximumFractionDigits: 3 }).format(value);

function compact(facts: (AnswerFact | null | false | undefined | '')[]): AnswerFact[] {
  return facts.filter((fact): fact is AnswerFact => Boolean(fact && fact.value));
}

function fact(label: string, value: string | null | undefined, extra: Partial<AnswerFact> = {}): AnswerFact | null {
  return value ? { label, value, ...extra } : null;
}

function baseActions(hit: SearchHit): HitAction[] {
  const to = featureRoute(hit.type, hit.id);
  return [
    { id: 'open', kind: 'open', label: 'Öffnen', icon: ArrowRight, to },
    { id: 'background', kind: 'background', label: 'In neuem Tab öffnen', hint: 'im Hintergrund, die Suche bleibt offen', icon: ExternalLink, to },
  ];
}

function copyAction(copy: CopyTarget | null): HitAction[] {
  return copy ? [{ id: 'copy', kind: 'copy', label: `${copy.what} kopieren`, icon: Copy, copy }] : [];
}

/** Summe mehrerer Beträge — `null` bei gemischten Währungen (dann lieber keine Zahl als eine falsche). */
export function sumMoney(values: MoneyDto[]): MoneyDto | null {
  if (values.length === 0) return null;
  const currency = values[0].currency;
  if (values.some((value) => value.currency !== currency)) return null;
  const amount = values.reduce((total, value) => total + BigInt(value.amount), 0n);
  return { amount: amount.toString(), currency };
}

/** Ohne Detaildaten: Antwort aus dem Treffer allein (alle Typen ohne eigene Antwort, und solange geladen wird). */
export function fallbackAnswer(hit: SearchHit): HitAnswer {
  // Belege tragen ihre Nummer als Titel (Such-Backend) — kopierbar, noch bevor die Details da sind.
  const copy: CopyTarget =
    hit.type === 'sales-document'
      ? { value: hit.title, what: 'Belegnummer' }
      : hit.type === 'purchase-order'
        ? { value: hit.title, what: 'Bestellnummer' }
        : { value: hit.title, what: 'Bezeichnung' };
  return { headline: null, facts: [], actions: [...baseActions(hit), ...copyAction(copy)], copy };
}

export function articleAnswer(
  hit: SearchHit,
  article: ArticleResponse,
  incoming: OpenIncomingEntry | null,
  can: Can,
): HitAnswer {
  const unit = unitLabel(article.unit);
  const isBelowMin = article.minStock != null && article.available < article.minStock;
  const copy: CopyTarget = { value: article.number, what: 'Artikelnummer' };
  const actions: HitAction[] = [
    ...baseActions(hit),
    {
      id: 'movements',
      kind: 'link',
      label: 'Lagerbewegungen ansehen',
      icon: History,
      to: `/einkauf/${article.id}?reiter=Lagerbewegungen`,
    },
  ];
  if (incoming && can('procurement.read')) {
    actions.push({
      id: 'incoming-order',
      kind: 'link',
      label: `Bestellung ${incoming.orderNumber} öffnen`,
      hint: 'die mit dem frühesten Liefertermin',
      icon: Truck,
      to: `/beschaffung/${incoming.orderId}`,
    });
  }
  return {
    headline: {
      label: 'Frei verfügbar',
      value: `${num(article.available)} ${unit}`,
      tone: article.available <= 0 ? 'danger' : isBelowMin ? 'warning' : undefined,
    },
    facts: compact([
      fact('Lagerbestand', `${num(article.stockQty)} ${unit}`),
      fact('Reserviert', `${num(article.reserved)} ${unit}`),
      incoming
        ? fact(
            'Bestellt, unterwegs',
            `${num(incoming.orderedQty)} ${unit}${incoming.nextExpectedDate ? ` · ab ${formatDate(incoming.nextExpectedDate)}` : ''}`,
          )
        : fact('Bestellt, unterwegs', `0 ${unit}`),
      article.minStock != null && fact('Mindestbestand', `${num(article.minStock)} ${unit}`, isBelowMin ? { tone: 'warning' } : {}),
      article.salesPrice && fact('Verkaufspreis', `${formatMoney(article.salesPrice)} / ${unit}`),
      fact('Lieferant', article.defaultSupplierName),
      !article.isActive && fact('Status', 'Inaktiv', { tone: 'warning' }),
    ]),
    actions: [...actions, ...copyAction(copy)],
    copy,
  };
}

/** Offene Posten eines Kunden — die OP-Liste kennt den Kunden nur beim Namen (`partyName`). */
export function customerOpenItems(customer: Pick<CustomerResponse, 'name'>, items: OpenItemResponse[]) {
  const own = items.filter(
    (item) => item.direction === 'RECEIVABLE' && item.partyName === customer.name && BigInt(item.open.amount) > 0n,
  );
  const overdue = own.filter((item) => item.isOverdue);
  return { count: own.length, open: sumMoney(own.map((item) => item.open)), overdue: sumMoney(overdue.map((item) => item.open)) };
}

export function customerAnswer(
  hit: SearchHit,
  customer: CustomerResponse,
  primaryContact: ContactResponse | null,
  openItems: OpenItemResponse[] | null,
  can: Can,
): HitAnswer {
  const copy: CopyTarget = customer.number
    ? { value: customer.number, what: customer.type === 'SUPPLIER' ? 'Lieferantennummer' : 'Kundennummer' }
    : { value: customer.name, what: 'Name' };
  const summary = openItems ? customerOpenItems(customer, openItems) : null;
  const headline: AnswerFact | null = summary
    ? {
        label: 'Offene Posten',
        value:
          summary.count === 0
            ? 'nichts offen'
            : `${summary.open ? formatMoney(summary.open) : '—'} · ${summary.count === 1 ? '1 Beleg' : `${summary.count} Belege`}`,
        tone: summary.overdue ? 'danger' : undefined,
      }
    : null;
  const contactName = primaryContact ? `${primaryContact.firstName} ${primaryContact.lastName}`.trim() : null;
  const actions: HitAction[] = [...baseActions(hit)];
  if (customer.type !== 'SUPPLIER' && can('sales.create') && !customer.isBillingBlocked) {
    actions.push({
      id: 'new-quote',
      kind: 'link',
      label: `Neues Angebot für ${customer.name}`,
      hint: 'Kunde vorbelegt, gespeichert wird im Formular',
      icon: Plus,
      to: `/verkauf?neu=1&kind=QUOTE&customerId=${encodeURIComponent(customer.id)}`,
    });
  }
  if (summary && summary.count > 0) {
    actions.push({
      id: 'open-items',
      kind: 'link',
      label: 'Offene Posten ansehen',
      icon: Receipt,
      to: `/buchhaltung?q=${encodeURIComponent(customer.name)}&direction=RECEIVABLE`,
    });
  }
  if (customer.email) {
    actions.push({ id: 'mail', kind: 'external', label: 'E-Mail schreiben', icon: Mail, href: `mailto:${customer.email}` });
  }
  if (customer.phone) {
    actions.push({
      id: 'copy-phone',
      kind: 'copy',
      label: 'Telefonnummer kopieren',
      icon: Phone,
      copy: { value: customer.phone, what: 'Telefonnummer' },
    });
  }
  return {
    headline,
    facts: compact([
      customer.isBillingBlocked && fact('Belegsperre', 'keine Belege an diese Adresse', { tone: 'danger' }),
      fact('Hinweis', customer.warningText, { tone: 'warning' }),
      summary?.overdue && fact('Davon überfällig', formatMoney(summary.overdue), { tone: 'danger' }),
      fact('Ansprechpartner', contactName),
      fact('Telefon', customer.phone, { copyValue: customer.phone ?? undefined }),
      fact('E-Mail', customer.email, { copyValue: customer.email ?? undefined }),
      customer.paymentTermsDays != null && fact('Zahlungsziel', `${customer.paymentTermsDays} Tage`),
    ]),
    actions: [...actions, ...copyAction(copy)],
    copy,
  };
}

export function contactAnswer(hit: SearchHit, contact: ContactDirectoryItem): HitAnswer {
  const phone = contact.phone || contact.mobile;
  const copy: CopyTarget | null = phone
    ? { value: phone, what: 'Telefonnummer' }
    : contact.email
      ? { value: contact.email, what: 'E-Mail-Adresse' }
      : null;
  const actions: HitAction[] = [...baseActions(hit)];
  if (contact.email) {
    actions.push({ id: 'mail', kind: 'external', label: 'E-Mail schreiben', icon: Mail, href: `mailto:${contact.email}` });
  }
  actions.push({
    id: 'company',
    kind: 'link',
    label: `${contact.customerName} öffnen`,
    icon: Users,
    to: featureRoute('customer', contact.customerId),
  });
  return {
    headline: null,
    facts: compact([
      fact('Firma', contact.customerName),
      fact('Funktion', contact.position),
      fact('Abteilung', contact.department),
      fact('Telefon', contact.phone, { copyValue: contact.phone ?? undefined }),
      fact('Mobil', contact.mobile, { copyValue: contact.mobile ?? undefined }),
      fact('E-Mail', contact.email, { copyValue: contact.email ?? undefined }),
    ]),
    actions: [...actions, ...copyAction(copy)],
    copy,
  };
}

/** `leadLabel`: die gewählte Projektleitung (`projectLeadLabel`), nicht das alte Textfeld. */
export function projectAnswer(hit: SearchHit, project: ProjectResponse, can: Can, leadLabel: string | null): HitAnswer {
  const copy: CopyTarget = { value: project.code, what: 'Projektnummer' };
  const ampel = PROJECT_AMPEL_OPTIONS.find((option) => option.value === project.ampel)?.label ?? project.ampel;
  const ampelTone: FactTone = project.ampel === 'ROT' ? 'danger' : project.ampel === 'GELB' ? 'warning' : 'success';
  const due = project.deliveryDate ?? project.plannedEndDate;
  const actions: HitAction[] = [...baseActions(hit)];
  // „Zeit auf … buchen" mit vorbelegtem Projekt fehlt noch: Der Zeiterfassungs-Dialog
  // leert das Projekt, sobald Firma/Kategorie gewählt werden (useTimerCascade) — offene
  // Fachfrage. Bis dahin die Stunden des Projekts.
  actions.push({ id: 'hours', kind: 'link', label: 'Stunden ansehen', icon: CalendarClock, to: `/projekte/${project.id}?tab=stunden` });
  actions.push({ id: 'material', kind: 'link', label: 'Materialbedarf öffnen', icon: ClipboardList, to: `/materialbedarf/${project.id}` });
  if (can('sales.create')) {
    actions.push({
      id: 'new-quote',
      kind: 'link',
      label: `Neues Angebot für ${project.code}`,
      hint: project.customerId ? 'Projekt und Kunde vorbelegt' : 'Projekt vorbelegt',
      icon: Plus,
      // Der Anlegen-Dialog übernimmt bei einem Projekt allein den Kunden nicht (wie bei
      // der Auswahl von Hand) — deshalb beide mitgeben.
      to: `/verkauf?neu=1&kind=QUOTE${project.customerId ? `&customerId=${encodeURIComponent(project.customerId)}` : ''}&projectId=${encodeURIComponent(project.id)}`,
    });
  }
  return {
    headline: { label: 'Status', value: projectStatusLabel(project.status) },
    facts: compact([
      fact('Termin-Ampel', ampel, { tone: ampelTone }),
      fact('Kunde', project.customerName),
      project.orderValue && fact('Auftragswert', formatMoney(project.orderValue)),
      due && fact(project.deliveryDate ? 'Liefertermin' : 'Geplantes Ende', formatDate(due)),
      fact('Projektleitung', leadLabel),
    ]),
    actions: [...actions, ...copyAction(copy)],
    copy,
  };
}

const INVOICE_KINDS = new Set<SalesDocumentResponse['kind']>(['INVOICE', 'ADVANCE_INVOICE', 'FINAL_INVOICE']);

export function salesDocumentAnswer(
  hit: SearchHit,
  doc: SalesDocumentResponse,
  openItem: OpenItemResponse | null,
  can: Can,
): HitAnswer {
  const copy: CopyTarget = { value: doc.number, what: 'Belegnummer' };
  const isOpen = openItem != null && BigInt(openItem.open.amount) > 0n;
  const actions: HitAction[] = [...baseActions(hit)];
  if (can('sales.create') && doc.status !== 'CANCELLED' && convertTargets(doc.kind, doc.status).length > 0) {
    actions.push({
      id: 'convert',
      kind: 'link',
      label: doc.kind === 'QUOTE' ? 'In Auftrag wandeln' : 'Beleg wandeln',
      hint: 'öffnet den Dialog im Beleg',
      icon: ArrowRightLeft,
      to: `/verkauf/${doc.id}?aktion=wandeln`,
    });
  }
  if (isOpen && can('accounting.create')) {
    actions.push({
      id: 'payment',
      kind: 'link',
      label: 'Zahlung erfassen',
      hint: 'in den offenen Posten',
      icon: CreditCard,
      to: `/buchhaltung?q=${encodeURIComponent(doc.number)}`,
    });
  }
  actions.push({ id: 'pdf', kind: 'link', label: 'PDF ansehen und senden', icon: FileText, to: `/verkauf/${doc.id}/pdf` });
  const dueLabel = INVOICE_KINDS.has(doc.kind) ? 'Fällig' : doc.kind === 'QUOTE' ? 'Gültig bis' : null;
  const dueValue = INVOICE_KINDS.has(doc.kind) ? doc.dueDate : doc.kind === 'QUOTE' ? doc.validUntil : null;
  return {
    headline: { label: 'Netto', value: formatMoney(doc.net) },
    facts: compact([
      isOpen && fact('Offen', formatMoney(openItem.open), { tone: openItem.isOverdue ? 'danger' : 'warning' }),
      openItem?.isOverdue && fact('Überfällig', `seit ${openItem.dueDate ? formatDate(openItem.dueDate) : '—'}`, { tone: 'danger' }),
      fact('Status', statusLabel(doc.status), doc.status === 'REJECTED' ? { tone: 'danger' } : {}),
      fact('Art', kindLabel(doc.kind)),
      fact('Kunde', doc.customerName),
      fact('Datum', formatDate(doc.documentDate)),
      dueLabel && dueValue && fact(dueLabel, formatDate(dueValue)),
      fact('Projekt', doc.projectCode),
    ]),
    actions: [...actions, ...copyAction(copy)],
    copy,
  };
}

export function purchaseOrderAnswer(hit: SearchHit, order: PurchaseOrderResponse): HitAnswer {
  const copy: CopyTarget = { value: order.number, what: 'Bestellnummer' };
  return {
    headline: { label: 'Netto', value: formatMoney(order.net) },
    facts: compact([
      fact('Status', purchaseStatusLabel(order.status, order.kind)),
      fact('Art', purchaseKindLabel(order.kind)),
      fact('Lieferant', order.supplierName),
      fact('Datum', formatDate(order.orderDate)),
      order.expectedDate && fact('Liefertermin', formatDate(order.expectedDate)),
      fact('Projekt', order.projectCode),
    ]),
    actions: [
      ...baseActions(hit),
      { id: 'pdf', kind: 'link', label: 'PDF ansehen und senden', icon: FileText, to: `/beschaffung/${order.id}/pdf` },
      ...copyAction(copy),
    ],
    copy,
  };
}
